import React, { useState, useEffect, forwardRef } from 'react';
import { Check, Mail, X, Loader2 } from 'lucide-react';
import { useRef } from 'react';
import patternImage from '../assets/z-monogram-pattern.png';
import { brandContent } from '../data/mock';

const FOUNDING_DEADLINE = new Date('2026-05-03T23:59:59+05:30').getTime();

const getEmailCopy = (launchTimestamp) => {
  const now = Date.now();
  const isLaunched = now >= launchTimestamp;
  const isFoundingWindow = isLaunched && now <= FOUNDING_DEADLINE;

  if (!isLaunched) {
    return {
      topText: brandContent.earlyAccessCTA.text,
      heading: 'ZURIAN Reserve',
      subheading: 'Access exclusively opens to founding bearers.',
      buttonLabel: 'UNLOCK PRIVILEGES',
      footer: "With your email, you will receive Reserve's access, updates, knowledge on THE ZURIAN WAY and founding privileges. We will never share any of your details.",
    };
  }
  if (isFoundingWindow) {
    return {
      topText: 'ZURIAN is now live. The first 14 days are your window to join as a Founding Member — part of the first circle of ZURIANS, with exclusive founding privileges.',
      heading: 'Become a Founding Member',
      subheading: 'Founding membership closes May 3rd.',
      buttonLabel: 'CLAIM FOUNDING STATUS',
      footer: 'Founding members receive exclusive access, early drops, and a permanent place in The ZURIAN story. We will never share your details.',
    };
  }
  return {
    topText: 'Stay connected with ZURIAN. Receive updates on new collections, the ZURIAN way of life, and exclusive offers for our community.',
    heading: 'Stay Connected',
    subheading: 'Join the ZURIAN community.',
    buttonLabel: 'JOIN THE CIRCLE',
    footer: "You'll receive updates on new collections, knowledge on THE ZURIAN WAY, and exclusive offers. We will never share your details.",
  };
};

const InteractiveButton = ({ isSubmitting, isValidEmail, label }) => {
  const [isInteracting, setIsInteracting] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [showValidAnimation, setShowValidAnimation] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isValidEmail) {
      setShowValidAnimation(true);
    } else {
      setShowValidAnimation(false);
    }
  }, [isValidEmail]);

  const handleInteraction = (e) => {
    setIsInteracting(true);
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();

    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });
  };

  const backgroundStyle = {
    background: isInteracting
      ? `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, #FFFFFF 0%, #EAEAEA 35%, #FFDF73 75%, #D4AF37 100%)`
      : 'linear-gradient(135deg, #FFFFFF 0%, #EAEAEA 20%, #FFFFFF 40%, #FFDF73 60%, #D4AF37 80%, #FFDF73 100%)',
    backgroundSize: isInteracting ? '100% 100%' : '200% 200%',
    animation: isInteracting ? 'none' : 'gradientFlow 15s ease infinite',
    cursor: isValidEmail ? 'pointer' : 'not-allowed',
  };

  return (
    <button
      ref={buttonRef}
      type="submit"
      aria-disabled={isSubmitting || !isValidEmail}
      onClick={(e) => {
        if (!isValidEmail || isSubmitting) e.preventDefault();
      }}
      onMouseEnter={handleInteraction}
      onMouseMove={handleInteraction}
      onMouseLeave={() => setIsInteracting(false)}
      onTouchStart={handleInteraction}
      onTouchMove={handleInteraction}
      onTouchEnd={() => setIsInteracting(false)}
      style={backgroundStyle}
      className={`relative w-fit mx-auto flex items-center justify-center gap-2 font-bold text-sm sm:text-base px-6 sm:px-10 md:px-12 py-2 sm:py-3 transition-all duration-500 text-primary-navy mt-[34px] rounded-[50px] border-2 ${isValidEmail ? 'border-[#F8E29E] shadow-[0_0_35px_rgba(248,226,158,0.8)] scale-[1.02]' : 'border-transparent shadow-lg hover:shadow-xl'
        }`}
    >
      <span className="relative z-10 flex items-center justify-center gap-2">
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Registering...</span>
          </>
        ) : (
          label
        )}
      </span>
    </button>
  );
};



const EmailCapture = forwardRef((props, ref) => {
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const copy = getEmailCopy(brandContent.launchTimestamp);

  // Check localStorage for existing submission
  useEffect(() => {
    const hasSubmitted = localStorage.getItem('zurian_subscribed');
    const hasDismissed = localStorage.getItem('zurian_subscribed_dismissed');
    if (hasSubmitted && !hasDismissed) {
      setIsSuccess(true);
    }
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleReset = () => {
    localStorage.setItem('zurian_subscribed_dismissed', 'true');
    setIsSuccess(false);
    setEmail('');
    setIsValidEmail(false);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setIsValidEmail(validateEmail(value));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Server error');
      }

      const result = await response.json();

      if (result.success) {
        setIsSuccess(true);
        localStorage.setItem('zurian_subscribed', 'true');
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Subscribe error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section
        id="email-capture"
        ref={ref}
        className="relative z-20 pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20 bg-primary-beige px-4 sm:px-6 scroll-mt-24"
      >

        {/* Early Access Text */}
        <div className="relative z-10 max-w-2xl mx-auto px-2 md:px-12 text-center mb-[5.5rem] mt-4">
          <p className="font-body text-sm sm:text-base md:text-lg leading-relaxed text-primary-royal-blue font-medium">
            {copy.topText}
          </p>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(20,31,87,0.2),_0_80px_100px_-24px_rgba(20,31,87,0.8)] bg-primary-navy border border-[#D4AF37]/20">
          <div className="relative p-10 md:p-14 text-center animate-scale-in">
            <button
              onClick={handleReset}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center justify-center mb-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center bg-[#D4AF37]/20 border border-[#D4AF37]/30"
              >
                <Check className="w-8 h-8 text-[#D4AF37]" />
              </div>
            </div>
            <h3 className="font-heading text-2xl md:text-3xl text-brand-gradient-gold-end mb-3">
              You're on our exclusive list
            </h3>
            <p className="font-body text-base text-primary-beige/80">
              Watch your inbox for updates.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="email-capture"
      ref={ref}
      className="relative z-20 pt-12 md:pt-16 pb-12 sm:pb-16 md:pb-20 bg-primary-beige px-4 sm:px-6 scroll-mt-24"
    >

      {/* Simplified Raindrop Pattern using standard CSS masks that work securely */}
      <div className="absolute inset-0 pointer-events-none z-[10] overflow-hidden">
        {/* Left rain column */}
        <div className="absolute top-0 left-[-5%] w-[35%] h-full opacity-80 mix-blend-multiply animate-rain-flow-right"
          style={{
            backgroundImage: `url(${patternImage})`,
            backgroundSize: '720px',
            backgroundRepeat: 'repeat',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 60%, transparent 100%), linear-gradient(to right, black 0%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 60%, transparent 100%), linear-gradient(to right, black 0%, transparent 100%)',
            maskComposite: 'intersect',
            WebkitMaskComposite: 'source-in'
          }}
        />
        {/* Right rain column */}
        <div className="absolute top-[5%] right-[-5%] w-[35%] h-[95%] opacity-80 mix-blend-multiply animate-rain-flow-left"
          style={{
            backgroundImage: `url(${patternImage})`,
            backgroundSize: '720px',
            backgroundRepeat: 'repeat',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 60%, transparent 100%), linear-gradient(to left, black 0%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 60%, transparent 100%), linear-gradient(to left, black 0%, transparent 100%)',
            maskComposite: 'intersect',
            WebkitMaskComposite: 'source-in'
          }}
        />
      </div>

      {/* Early Access Text */}
      <div className="relative z-10 max-w-2xl mx-auto px-2 md:px-12 text-center mb-[5rem] mt-4">
        <p className="font-body text-sm sm:text-base md:text-lg leading-relaxed text-primary-royal-blue font-medium">
          {copy.topText}
        </p>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-[0_0_30px_rgba(20,31,87,0.2),_0_80px_100px_-24px_rgba(20,31,87,0.8)] bg-primary-navy border border-[#D4AF37]/20">
        <div className="relative z-10 max-w-md mx-auto px-8 md:px-0 min-h-[380px] flex flex-col py-6">

          {/* Section 1: Top (Heading) */}
          <div className="flex-none text-center mt-2 mb-4">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-brand-gradient-gold-end mb-1 leading-tight drop-shadow-sm">{copy.heading}</h2>
            <p className="font-body text-sm md:text-base text-primary-beige/80">{copy.subheading}</p>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
            {/* Section 2: Middle (Input) */}
            <div className="flex-1 flex items-center justify-center pt-6 md:pt-8">
              <div className="relative w-full flex items-center group">
                <div className="absolute left-0 w-12 h-full flex items-center justify-center pointer-events-none">
                  <Mail className="w-5 h-5 text-primary-beige/50 group-focus-within:text-[#D4AF37] transition-colors" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Your email address"
                  className="w-full py-4 pl-12 pr-4 font-body text-sm bg-white/5 border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:bg-white/10 outline-none transition-all duration-300 text-white rounded-md placeholder:text-white/40"
                  required
                />
              </div>
            </div>

            {/* Section 3: Bottom (Button & Error) */}
            <div className="flex-none flex flex-col mt-4 mb-2">
              {error && (
                <p className="font-body text-sm text-center text-red-500 mb-2">
                  {error}
                </p>
              )}
              <InteractiveButton
                isSubmitting={isSubmitting}
                isValidEmail={isValidEmail}
                label={copy.buttonLabel}
              />
              <p className="font-body text-[10px] sm:text-xs text-center mt-3 text-primary-beige/50">
                {copy.footer}
              </p>
            </div>
          </form>

        </div>
      </div>
    </section >
  );
});

export default EmailCapture;
